import React, { useState } from "react";
import { Grid, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, TextField, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import { Delete, AddCircleOutline, Edit, Save, Cancel } from "@mui/icons-material";
import { product } from "../../../api/paquetes";

export interface Pasajero { 
  nombre: string; 
  dni: string; 
  pasaporte: string;
  fechaNacimiento: string;
}

export interface PassengersSectionProps {
  pasajeros: Pasajero[];
  nuevoPasajero: Pasajero;
  setNuevoPasajero: (p: Pasajero) => void;
  eliminarPasajero: (pasajero: any) => void;
  paqueteId: number;
  onAddPassenger: (nuevo: Pasajero) => void;
  onUpdatePassenger: (editado: Pasajero) => void;
}

export const PassengersSection: React.FC<PassengersSectionProps> = ({ pasajeros = [], nuevoPasajero, setNuevoPasajero, eliminarPasajero, paqueteId, onAddPassenger, onUpdatePassenger }) => {
  const [cargando, setCargando] = useState(false);
  const [errorMensaje, setErrorMensaje] = useState<string | null>(null);
  const [dialogoEliminar, setDialogoEliminar] = useState(false);
  const [pasajeroAEliminar, setPasajeroAEliminar] = useState<any>(null);
  const [editando, setEditando] = useState<number | null>(null);
  const [pasajeroEditado, setPasajeroEditado] = useState<any>(null);

  const validarDNI = (dni: string | number) => {
    const limpio = String(dni).trim();
    if (!limpio) return "El DNI es requerido";
    if (!/^\d{7,9}$/.test(limpio)) return "DNI inválido (7-9 dígitos)";
    return null;
  };

  const existePasajero = (dni: string | number) => {
    return (pasajeros || []).some((p: any) => String(p.dni).trim() === String(dni).trim());
  };

  const agregarPasajero = async () => {
    setErrorMensaje(null);
    if (!nuevoPasajero.nombre?.trim()) { setErrorMensaje("El nombre es requerido"); return; }
    const errDni = validarDNI(nuevoPasajero.dni);
    if (errDni) { setErrorMensaje(errDni); return; }
    if (existePasajero(nuevoPasajero.dni)) { setErrorMensaje("Ya existe un pasajero con ese DNI"); return; }
    setCargando(true);
    try {
      const pasajeroData = {
        nombre: nuevoPasajero.nombre,
        dni: nuevoPasajero.dni,
        pasaporte: nuevoPasajero.pasaporte || null,
        fecha_nacimiento: nuevoPasajero.fechaNacimiento || null
      };
      
      const response = await product.createPasajero(paqueteId, pasajeroData);
      
      const pasajeroCreado = response.data?.data || response.data;
      const pasajeroMapeado = {
        id: pasajeroCreado.id,
        nombre: pasajeroCreado.nombre || pasajeroCreado.apellido_nombre || nuevoPasajero.nombre,
        dni: pasajeroCreado.dni || pasajeroCreado.documento || nuevoPasajero.dni,
        pasaporte: pasajeroCreado.pasaporte || nuevoPasajero.pasaporte || '',
        fechaNacimiento: pasajeroCreado.fecha_nacimiento || nuevoPasajero.fechaNacimiento || ''
      };
      
      onAddPassenger(pasajeroMapeado);
      setNuevoPasajero({ nombre: "", dni: "", pasaporte: "", fechaNacimiento: "" });
      setErrorMensaje(null);
    } catch (error: any) {
      const mensaje = error?.response?.data?.message || "Error al agregar el pasajero";
      setErrorMensaje(mensaje);
    } finally {
      setCargando(false);
    }
  };

  const handleEliminarClick = (pasajero: any) => {
    setPasajeroAEliminar(pasajero);
    setDialogoEliminar(true);
  };

  const confirmarEliminar = () => {
    if (pasajeroAEliminar) {
      eliminarPasajero(pasajeroAEliminar);
    }
    setDialogoEliminar(false);
    setPasajeroAEliminar(null);
  };

  const cancelarEliminar = () => {
    setDialogoEliminar(false);
    setPasajeroAEliminar(null);
  };

  const handleEditarClick = (pasajero: any, index: number) => {
    setEditando(index);
    setPasajeroEditado({ 
      ...pasajero,
      fechaNacimiento: pasajero.fechaNacimiento || pasajero.fecha_nacimiento || ''
    });
  };

  const handleCancelarEdicion = () => {
    setEditando(null);
    setPasajeroEditado(null);
  };

  const handleGuardarEdicion = async () => {
    if (!pasajeroEditado || !pasajeroEditado.id) return;
    
    const errDni = validarDNI(pasajeroEditado.dni);
    if (errDni) { 
      setErrorMensaje(errDni); 
      return; 
    }
    
    if (!pasajeroEditado.nombre?.trim()) { 
      setErrorMensaje("El nombre es requerido"); 
      return; 
    }

    setCargando(true);
    setErrorMensaje(null);
    
    try {
      await product.updatePasajero(paqueteId, pasajeroEditado.id, {
        nombre: pasajeroEditado.nombre,
        dni: pasajeroEditado.dni,
        pasaporte: pasajeroEditado.pasaporte || null,
        fecha_nacimiento: pasajeroEditado.fechaNacimiento || null
      });
      
      // Notificar al padre para actualizar el estado con el pasajero editado
      onUpdatePassenger(pasajeroEditado);
      
      setEditando(null);
      setPasajeroEditado(null);
    } catch (error: any) {
      const mensaje = error?.response?.data?.message || "Error al actualizar el pasajero";
      setErrorMensaje(mensaje);
    } finally {
      setCargando(false);
    }
  };

  return (
    <Grid item xs={12}>
      <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>PASAJEROS</Typography>
        {errorMensaje && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>{errorMensaje}</Typography>
        )}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '10%' }}>Acciones</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '30%' }}>Apellido y Nombre</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '15%' }}>DNI</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '20%' }}>Pasaporte</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '25%' }}>Fecha Nac.</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(pasajeros || []).map((px: any, idx: number) => (
                <TableRow key={idx}>
                  <TableCell>
                    {editando === idx ? (
                      <>
                        <IconButton size="small" color="success" onClick={handleGuardarEdicion} disabled={cargando}>
                          <Save fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="default" onClick={handleCancelarEdicion}>
                          <Cancel fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton size="small" color="primary" onClick={() => handleEditarClick(px, idx)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleEliminarClick(px)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {editando === idx ? (
                      <TextField 
                        size="small" 
                        fullWidth 
                        value={pasajeroEditado?.nombre || ''} 
                        onChange={(e) => setPasajeroEditado({ ...pasajeroEditado, nombre: e.target.value })}
                      />
                    ) : (
                      px.nombre
                    )}
                  </TableCell>
                  <TableCell>
                    {editando === idx ? (
                      <TextField 
                        size="small" 
                        value={pasajeroEditado?.dni || ''} 
                        onChange={(e) => setPasajeroEditado({ ...pasajeroEditado, dni: e.target.value })}
                      />
                    ) : (
                      px.dni
                    )}
                  </TableCell>
                  <TableCell>
                    {editando === idx ? (
                      <TextField 
                        size="small" 
                        value={pasajeroEditado?.pasaporte || ''} 
                        onChange={(e) => setPasajeroEditado({ ...pasajeroEditado, pasaporte: e.target.value })}
                        placeholder="Pasaporte"
                      />
                    ) : (
                      px.pasaporte || '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {editando === idx ? (
                      <TextField 
                        size="small" 
                        type="date"
                        value={pasajeroEditado?.fechaNacimiento || ''}
                        onChange={(e) => setPasajeroEditado({ ...pasajeroEditado, fechaNacimiento: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : (
                      px.fechaNacimiento || px.fecha_nacimiento || '-'
                    )}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell>
                  <IconButton size="small" color="primary" onClick={agregarPasajero} disabled={cargando}>
                    <AddCircleOutline />
                  </IconButton>
                </TableCell>
                <TableCell>
                  <TextField size="small" fullWidth value={nuevoPasajero.nombre} onChange={(e) => setNuevoPasajero({ ...nuevoPasajero, nombre: e.target.value })} placeholder="Apellido, Nombre" />
                </TableCell>
                <TableCell>
                  <TextField size="small" value={nuevoPasajero.dni} onChange={(e) => setNuevoPasajero({ ...nuevoPasajero, dni: e.target.value })} placeholder="DNI" />
                </TableCell>
                <TableCell>
                  <TextField size="small" value={nuevoPasajero.pasaporte || ''} onChange={(e) => setNuevoPasajero({ ...nuevoPasajero, pasaporte: e.target.value })} placeholder="Pasaporte" />
                </TableCell>
                <TableCell>
                  <TextField 
                    size="small" 
                    type="date"
                    value={nuevoPasajero.fechaNacimiento || ''} 
                    onChange={(e) => setNuevoPasajero({ ...nuevoPasajero, fechaNacimiento: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog de confirmación para eliminar */}
      <Dialog open={dialogoEliminar} onClose={cancelarEliminar}>
        <DialogTitle>Eliminar Pasajero</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea eliminar al pasajero <strong>{pasajeroAEliminar?.nombre}</strong>? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelarEliminar}>Cancelar</Button>
          <Button onClick={confirmarEliminar} color="error" autoFocus>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default PassengersSection;